import type {
	AtAGlanceRequestCallbacks,
	AtAGlanceResponse,
	AtAGlanceRequestOptions,
	AtAGlanceRequestDependencies,
} from './atAGlanceTypes.ts';
import {
	type AtAGlanceFailureReason,
	type AtAGlanceRecoveryAttributes,
	getServiceErrorCodeForAnalytics,
} from './atAGlanceAnalytics.ts';
import { AtAGlanceRequestError } from './atAGlanceRequestError.ts';
import { sendAtAGlanceRequest } from './sendAtAGlanceRequest.ts';
import { parseAtAGlanceOutput } from './parseAtAGlanceOutput.ts';
import {
	type AtAGlanceRetryReason,
	getQuotaCooldown,
	recordQuotaCooldown,
	scheduleAfter,
} from './atAGlanceRetry.ts';

const REQUEST_TIMEOUT_MS = 60_000;
const RECOVERY_BUDGET_MS = 120_000;

class AtAGlanceTimeoutError extends Error {
	constructor() {
		super('At a Glance recovery timed out');
		this.name = 'AtAGlanceTimeoutError';
	}
}

const isAbortError = (error: unknown): boolean =>
	typeof error === 'object' &&
	error !== null &&
	'name' in error &&
	(error as { name: string }).name === 'AbortError';

const toError = (error: unknown): Error =>
	error instanceof Error ? error : new Error(String(error));

export const startAtAGlanceRequest = (
	{ cloudId, requestBody }: AtAGlanceRequestOptions,
	{ fetch: fetchRequest }: AtAGlanceRequestDependencies,
	{ onSuccess, onFailure, onRetryState, onAborted }: AtAGlanceRequestCallbacks,
): (() => void) => {
	const controller = new AbortController();
	const startedAt = performance.now();
	let cancelled = false;
	let settled = false;
	let cancelRetryTimer: (() => void) | undefined;
	let requestTimer: ReturnType<typeof setTimeout> | undefined;
	let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
	const clearRequestTimers = () => {
		clearTimeout(requestTimer);
		clearTimeout(recoveryTimer);
		cancelRetryTimer?.();
	};
	let retryCount = 0;
	let retryReason: AtAGlanceRetryReason | undefined;
	let serviceErrorCode: string | undefined;
	let retryDelayMs = 0;
	const recoveryAttributes = (
		recoveryOutcome: AtAGlanceRecoveryAttributes['recoveryOutcome'],
	): AtAGlanceRecoveryAttributes => ({
		...(serviceErrorCode ? { serviceErrorCode } : {}),
		...(retryReason ? { retryCount, retryReason, retryDelayMs, recoveryOutcome } : {}),
	});
	const elapsedMs = () => Math.round(performance.now() - startedAt);

	const reportFailure = (reason: AtAGlanceFailureReason, failure: Error, statusCode?: number) => {
		if (cancelled || settled) {
			return;
		}
		settled = true;
		clearRequestTimers();
		onFailure(failure, {
			reason,
			statusCode,
			durationMs: elapsedMs(),
			...recoveryAttributes('exhausted'),
		});
	};

	const setRetryState = (retryPending: boolean) => {
		onRetryState(retryPending, retryPending ? retryReason : undefined);
	};

	const onTimeout = () => {
		reportFailure('timeout', new AtAGlanceTimeoutError());
		controller.abort();
	};
	recoveryTimer = setTimeout(onTimeout, RECOVERY_BUDGET_MS);

	const handleRequestFailure = (caught: unknown, allowAutomaticRetry: boolean) => {
		const requestError = caught instanceof AtAGlanceRequestError ? caught : undefined;
		serviceErrorCode = getServiceErrorCodeForAnalytics(requestError?.code);
		const rateLimited = requestError?.retryReason === 'rateLimited';
		const retryNotBefore = requestError?.retryReason
			? Date.now() + requestError.retryAfterMs + 250 + Math.floor(Math.random() * 1000)
			: 0;
		if (rateLimited) recordQuotaCooldown(cloudId, retryNotBefore);
		if (requestError?.retryReason && allowAutomaticRetry) {
			retryReason = requestError.retryReason;
			retryDelayMs += Math.max(0, retryNotBefore - Date.now());
			setRetryState(true);
			cancelRetryTimer = scheduleAfter(retryNotBefore, () => {
				setRetryState(false);
				runRequest(false);
			});
			return;
		}
		reportFailure(
			rateLimited ? 'rateLimited' : requestError ? 'httpError' : 'networkError',
			toError(caught),
			requestError?.status,
		);
	};

	const handleResponse = (response: AtAGlanceResponse, responseBody: unknown) => {
		clearTimeout(requestTimer);
		if (cancelled || settled) {
			return;
		}
		if (!response.ok) {
			throw new AtAGlanceRequestError(
				response.status,
				responseBody,
				response.headers.get('Retry-After'),
			);
		}
		const parsed = parseAtAGlanceOutput(responseBody);
		if (!parsed) {
			reportFailure('malformedResponse', new Error('At a Glance response envelope is malformed'));
			return;
		}
		settled = true;
		clearRequestTimers();
		onSuccess(parsed, {
			durationMs: elapsedMs(),
			...recoveryAttributes('recovered'),
		});
	};

	const runRequest = (allowAutomaticRetry: boolean) => {
		if (cancelled) {
			return;
		}
		const quotaNotBefore = getQuotaCooldown(cloudId);
		if (quotaNotBefore > Date.now()) {
			retryReason = 'rateLimited';
			serviceErrorCode = 'AI_FEATURE_RATE_LIMITED';
			retryDelayMs += quotaNotBefore - Date.now();
			setRetryState(true);
			cancelRetryTimer = scheduleAfter(quotaNotBefore, () => runRequest(allowAutomaticRetry));
			return;
		}
		if (!allowAutomaticRetry) retryCount = 1;
		setRetryState(false);
		requestTimer = setTimeout(onTimeout, REQUEST_TIMEOUT_MS);
		sendAtAGlanceRequest(
			{ cloudId, requestBody, signal: controller.signal },
			fetchRequest,
			handleResponse,
		).catch((caught: unknown) => {
			clearTimeout(requestTimer);
			if (cancelled || settled || isAbortError(caught)) {
				return;
			}
			handleRequestFailure(caught, allowAutomaticRetry);
		});
	};
	runRequest(true);

	return () => {
		cancelled = true;
		clearRequestTimers();
		controller.abort();
		if (!settled) {
			onAborted({
				durationMs: elapsedMs(),
				...recoveryAttributes(retryCount ? 'cancelledAfterRetry' : 'cancelledBeforeRetry'),
			});
		}
	};
};
